import { Capacitor } from "@capacitor/core";
import { SplashScreen } from "@capacitor/splash-screen";
import { StatusBar } from "@capacitor/status-bar";
import { NavigationBar } from "@hugotomazi/capacitor-navigation-bar";
import { isAxiosError } from "axios";
import { defineStore } from "pinia";
import { nextTick } from "vue";

import getFunderOverview from "@/modules/funder/api/getFunderOverview";
import { FunderOverview } from "@/modules/funder/types";
import dayjs from "@/shared/plugins/dayjs";

export const useFunderStore = defineStore("funder", {
  persist: true,
  state: (): {
    data?: FunderOverview;
    lastFetchedAt?: string;
    hasError: boolean;
    errorCode?: number;
  } => ({
    hasError: false,
    errorCode: undefined,
    data: undefined,
    lastFetchedAt: undefined
  }),
  actions: {
    async fetchFunder() {
      if (this._shouldFetchData()) {
        await this._fetchFunderData();
      }
      setTimeout(async () => {
        await SplashScreen.hide();
        await nextTick();
        setTimeout(() => {
          if (Capacitor.isPluginAvailable("StatusBar")) {
            StatusBar.setBackgroundColor({ color: "#2A455E" });
          }

          if (Capacitor.isPluginAvailable("NavigationBar")) {
            NavigationBar.setTransparency({ isTransparent: true });
          }
        }, 10);
      }, 500);
    },
    async forceFetchFunder() {
      await this._fetchFunderData();
    },
    async _fetchFunderData() {
      try {
        const response = await getFunderOverview();

        if (!response?.data) {
          this.data = undefined;
          this.hasError = true;
          this.errorCode = 500;
          return;
        }

        this._setFunderData(response.data);
        this.hasError = false;
        this.errorCode = undefined;
      } catch (error) {
        this.data = undefined;
        this.hasError = true;
        if (!isAxiosError(error)) {
          throw error;
        }
        if (!error.response?.status) {
          throw error;
        }
        if (error.response?.status === 401) {
          return;
        }
        this.errorCode = error.response?.status;
        throw error;
      }
    },
    _shouldFetchData(): boolean {
      return !this.lastFetchedAt || dayjs().diff(this.lastFetchedAt, "minutes") >= 5;
    },
    _setFunderData(data: FunderOverview) {
      this.data = data;
      this.lastFetchedAt = dayjs().toISOString();
    }
  },
  getters: {
    getContractById: (state) => (id: string) => {
      return state.data?.fundingContracts.find((contract) => contract.id === id);
    },
    isAllowedToManageContracts: (state) => {
      return state.data?.funder?.isAllowedToManageContracts || false;
    }
  }
});
